import React from 'react';

import { faFileArchive, faDownload, faFileAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cn } from '../../../helpers/shadCnUtils';
import { ActiveJob } from '@/hooks/useJobProgress';

interface ActiveUploadsProgressProps {
  activeJobs: ActiveJob[];
  zipIsProcessing: boolean;
}

export const ActiveUploadsProgress: React.FC<ActiveUploadsProgressProps> = ({ activeJobs, zipIsProcessing }) => {
  if (activeJobs.length === 0 && zipIsProcessing) {
    return (
      <div className="mt-3 space-y-2">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-blue-500" />
            <span className="text-sm font-medium">Waiting for Zip to begin processing...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      {activeJobs.map((job) => {
        const progressPercentage = job.totalFiles > 0 ? Math.round((job.processedFiles / job.totalFiles) * 100) : 0;

        const getJobIcon = () => {
          switch (job.type) {
            case 'unzip':
              return faFileArchive;
            case 'relativity':
              return faDownload;
            default:
              return faFileAlt;
          }
        };

        const getJobTypeLabel = () => {
          switch (job.type) {
            case 'unzip':
              return 'Extracting Zip';
            case 'relativity':
              return 'Relativity Import';
            default:
              return 'Processing';
          }
        };

        return (
          <div key={job.id} className="rounded-lg border border-gray-200 bg-white p-3">
            <>
              <div className="mb-2 flex items-center justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <FontAwesomeIcon icon={getJobIcon()} className={`text-brandSecondary h-3.5 w-3.5 flex-shrink-0`} />
                  <span className="truncate text-sm font-medium text-gray-900">{getJobTypeLabel()}</span>
                  <span className="truncate text-xs text-gray-500">{job.fileName}</span>
                </div>

                <span className="ml-2 flex-shrink-0 text-xs font-medium text-gray-900">
                  {job.totalFiles === 0
                    ? 'Preparing...'
                    : `${job.processedFiles}/${job.totalFiles} files (${progressPercentage}%)`}
                </span>
              </div>

              {/* Compact Progress Bar */}
              <div className="h-1.5 w-full rounded-full bg-gray-200">
                <div
                  className="bg-brandSecondary h-1.5 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </>
          </div>
        );
      })}
    </div>
  );
};
